import { useNavigate } from 'react-router';
import { useOnboarding } from "../../../../context/OnboardingContext";
import { useEffect, useState } from "react";
import { toast } from "react-toast";
import { STEP_STATUS } from "../../../../Config/Onboarding/constant";
import { PrivateWizardLayout, WizardActions, WizardCard, WizardContainer } from "../../WizardLayout";
import Stepper from "../../Stepper";
import { LuPlay, LuSkipForward,  LuZap, LuChevronDown, LuLoader, LuCircleCheck, LuCircleX, LuX } from "react-icons/lu";
import { useGetIOTTestCases, useSaveExecutionScripts } from "../../../../hooks/API/wizard.api";

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = { IDLE: "idle", RUNNING: "running", PASSED: "passed", FAILED: "failed" };

const statusStyle = {
  [STATUS.IDLE]:    { bg: "#f8fafc", border: "#e2e8f0", badge: "#f1f5f9", badgeText: "#64748b", label: "Not Run" },
  [STATUS.RUNNING]: { bg: "#eff6ff", border: "#bfdbfe", badge: "#dbeafe", badgeText: "#2563eb", label: "Running…" },
  [STATUS.PASSED]:  { bg: "#f0fdf4", border: "#bbf7d0", badge: "#dcfce7", badgeText: "#16a34a", label: "Passed" },
  [STATUS.FAILED]:  { bg: "#fef2f2", border: "#fecaca", badge: "#fee2e2", badgeText: "#dc2626", label: "Failed" },
};

// ─── Small helpers ────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = statusStyle[status] || statusStyle[STATUS.IDLE];
  const icons = {
    [STATUS.IDLE]:    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#94a3b8", display: "inline-block" }} />,
    [STATUS.RUNNING]: <LuLoader size={11} style={{ animation: "spin 1s linear infinite" }} />,
    [STATUS.PASSED]:  <LuCircleCheck size={11} />,
    [STATUS.FAILED]:  <LuCircleX size={11} />,
  };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: "0.7rem", fontWeight: 700,
      background: s.badge, color: s.badgeText,
      border: `1px solid ${s.badgeText}30`,
      borderRadius: 20, padding: "3px 9px",
    }}>
      {icons[status]} {s.label}
    </span>
  );
};

const MiniProgressBar = ({ value }) => (
  <div style={{ height: 4, background: "#e2e8f0", borderRadius: 99, overflow: "hidden", marginTop: 6 }}>
    <div style={{
      height: "100%", borderRadius: 99,
      width: `${value}%`,
      background: "linear-gradient(90deg, #6366f1, #06b6d4)",
      transition: "width 0.4s ease",
    }} />
  </div>
);

// ─── Main Compo nent ───────────────────────────────────────────────────────────
const Step12IotRun = () => {
  const navigate = useNavigate();
  const { setRunJob, nextStep, previousStep, data, markStepCompleted, setStepStatus,goToStep } = useOnboarding();

  const projectId = data?.project?.project_id
  const { data: iotTestCases, isLoading } = useGetIOTTestCases({ projectId });
  const { mutate: saveExecutionScripts } = useSaveExecutionScripts();
  const testCases = iotTestCases?.data || [];
  const deviceAddress = iotTestCases?.device_address;

  // State for checkbox selection
  const [selectedTestCases, setSelectedTestCases] = useState({});
  const [batchResults, setBatchResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // per-test-case state: { [id]: { status, progress, passed, failed, total } }
  const [tcState, setTcState] = useState({});
  const [openCards, setOpenCards] = useState({});

  // ── Helpers ────────────────────────────────────────────────────────────────
  const updateTc = (id, patch) =>
    setTcState(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const toggleCard = (id) =>
    setOpenCards(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleTestCaseSelection = (id) => {
    setSelectedTestCases(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleSelectAll = () => {
    if (Object.values(selectedTestCases).every(v => v)) {
      // If all are selected, deselect all
      const newSelection = {};
      testCases.forEach(tc => newSelection[tc.test_case_id] = false);
      setSelectedTestCases(newSelection);
    } else {
      // Select all
      const newSelection = {};
      testCases.forEach(tc => newSelection[tc.test_case_id] = true);
      setSelectedTestCases(newSelection);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedTestCases).filter(Boolean).length;
  };

  const getSelectedTestCases = () => {
    return testCases.filter(tc => selectedTestCases[tc.test_case_id]);
  };

  const getStatus = (id) => tcState[id]?.status || "idle";

  // ── postMessage listener ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (event) => {
      const msg = event.data;
      if (msg?.source !== "ROBITEST_AGENT") return;

      if (msg.type === "EXECUTE_AUTOMATION_RESULT") {
        setIsExecuting(false);
        
        if (msg.success) {
          // Update test case states based on batch results
          msg.batch_report?.forEach(report => {
            updateTc(report.test_case_id, {
              status: report.success ? "passed" : "failed",
              results: report.results,
            });
          });
          
          // Save execution scripts with the full message payload
          saveExecutionScripts(msg, {
            onSuccess: () => {
              console.log("Execution scripts saved successfully");
            },
            onError: (error) => {
              console.error("Failed to save execution scripts:", error);
            }
          });
          
          setBatchResults(msg);
          setShowResultsModal(true);
          toast.success("Batch execution completed!");
        } else {
          toast.error(msg.message || "Batch execution failed");
          setBatchResults(msg);
          setShowResultsModal(true);
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [saveExecutionScripts]);

  // ── Run single ─────────────────────────────────────────────────────────────
  const runSingle = (tc) => {
    if (!deviceAddress) {
      toast.error("Device address not found!");
      return;
    }
    
    setIsExecuting(true);
    
    const payload = {
      device_address: deviceAddress,
      test_cases: [
        {
          test_case_id: tc.test_case_id,
          steps: tc.steps,
        }
      ]
    };

    window.postMessage({
      source: "ROBITEST_CLOUD",
      action: "EXECUTE_AUTOMATION_REQUEST",
      payload,
    }, "*");

    toast.info(`Executing test case: ${tc.name}`);
  };

  // ── Run selected ───────────────────────────────────────────────────────────
  const runSelected = () => {
    const selected = getSelectedTestCases();
    if (!selected.length) {
      toast.error("No test cases selected!");
      return;
    }

    if (!deviceAddress) {
      toast.error("Device address not found!");
      return;
    }
    
    setIsExecuting(true);
    
    const payload = {
      device_address: deviceAddress,
      test_cases: selected.map(tc => ({
        test_case_id: tc.test_case_id,
        steps: tc.steps,
      }))
    };

    window.postMessage({
      source: "ROBITEST_CLOUD",
      action: "EXECUTE_AUTOMATION_REQUEST",
      payload,
    }, "*");

    toast.info(`Executing ${selected.length} test case${selected.length > 1 ? 's' : ''}...`);
  };

  // ── Skip ───────────────────────────────────────────────────────────────────
  const handleSkip = () => {
    // setRunJob({ runId: null, status: "skipped" });
    // nextStep();
    // navigate("/app/onboarding?step=11");
  };

  const handleBack = () => {
    previousStep();
    navigate("/app/onboarding?step=9");
  };

  const handleContinue = () => {
    markStepCompleted(10);
    setStepStatus(10, STEP_STATUS.COMPLETED);
     goToStep(11);
    // nextStep();
    navigate("/app/onboarding?step=11");
  };

  // ── Summary numbers ────────────────────────────────────────────────────────
  const summary = testCases.reduce((acc, tc) => {
    const s = getStatus(tc.test_case_id);
    if (s === "passed") acc.passed++;
    else if (s === "failed") acc.failed++;
    return acc;
  }, { passed: 0, failed: 0 });

  const selectedCount = getSelectedCount();
  const allTestCasesSelected = selectedCount === testCases.length && testCases.length > 0;

  // ── Priority config (reuse from step9) ────────────────────────────────────
  const priorityConfig = {
    low:    { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e", label: "Low" },
    medium: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", dot: "#f59e0b", label: "Medium" },
    high:   { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", dot: "#ef4444", label: "High" },
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={12} completedSteps={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]} />

        <WizardCard
          title="Run Your IoT Test Cases"
          subtitle="Execute individual tests or run the entire suite at once"
        >
          {/* ── Top summary bar ── */}
          {testCases.length > 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: "0.75rem 1rem",
              marginBottom: "1.25rem",
              gap: "0.75rem",
              flexWrap: "wrap",
            }}>
              {/* stat pills */}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                {[
                  { label: "Total",   value: testCases.length, bg: "#f1f5f9", text: "#475569", border: "#e2e8f0" },
                  { label: "Passed",  value: summary.passed,   bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
                  { label: "Failed",  value: summary.failed,   bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
                  { label: "Selected", value: selectedCount,  bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" },
                ].map(stat => (
                  <div key={stat.label} style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: stat.bg,
                    border: `1px solid ${stat.border}`,
                    borderRadius: 8, padding: "0.3rem 0.75rem",
                  }}>
                    <span style={{ fontSize: "0.95rem", fontWeight: 800, color: stat.text, lineHeight: 1 }}>
                      {stat.value}
                    </span>
                    <span style={{ fontSize: "0.68rem", color: stat.text, fontWeight: 600, opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
 
              {/* Run button with selected count */}
              <button
                onClick={runSelected}
                disabled={selectedCount === 0 || isExecuting || isLoading}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "0.5rem 1.1rem",
                  background: selectedCount === 0 || isExecuting ? "#f1f5f9" : "linear-gradient(135deg, #6366f1, #818cf8)",
                  color: selectedCount === 0 || isExecuting ? "#94a3b8" : "white",
                  border: selectedCount === 0 || isExecuting ? "1px solid #e2e8f0" : "none",
                  borderRadius: 9,
                  fontSize: "0.8rem", fontWeight: 700,
                  cursor: selectedCount === 0 || isExecuting ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  boxShadow: (selectedCount === 0 || isExecuting) ? "none" : "0 2px 10px #6366f130",
                  whiteSpace: "nowrap",
                }}
              >
                <LuZap size={13} />
                {isExecuting ? "Running…" : `Run (${selectedCount})`}
              </button>
            </div>
          )}
 

          {/* ── Test case list ── */}
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
              <LuLoader size={28} style={{ animation: "spin 1s linear infinite", marginBottom: 12 }} />
              <div style={{ fontSize: "0.9rem" }}>Loading test cases…</div>
            </div>
          ) : testCases.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem", color: "#94a3b8" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>🧪</div>
              <div style={{ fontSize: "0.9rem" }}>No test cases found for this project.</div>
            </div>
          ) : (
            <>
              {/* Select All option */}
              <div style={{
                display: "flex", alignItems: "center", gap: "0.75rem",
                padding: "0.75rem 1rem",
                background: "#f8fafc",
                borderRadius: 8,
                marginBottom: "1rem",
                border: "1px solid #e2e8f0",
              }}>
                <input
                  type="checkbox"
                  checked={allTestCasesSelected}
                  onChange={toggleSelectAll}
                  style={{
                    width: 18,
                    height: 18,
                    cursor: "pointer",
                    accentColor: "#6366f1",
                  }}
                />
                <span style={{
                  fontWeight: 600,
                  color: "#0f172a",
                  fontSize: "0.95rem",
                }}>
                  Select All
                </span>
              </div>

              {/* Test cases grid */}
              <div style={{ display: "grid", gap: "1rem" }}>
                {testCases.map((tc) => {
                  const id = tc.test_case_id;
                  const st = getStatus(id);
                  const tcData = tcState[id] || {};
                  const steps = tc.steps?.step_description || [];
                  const isOpen = openCards[id] ?? false;
                  const cfg = priorityConfig[tc.priority] || priorityConfig.low;
                  const isSelected = selectedTestCases[id] ?? false;

                  return (
                    <div key={id} style={{
                      border: `1.5px solid ${isSelected ? "#6366f1" : "#e2e8f0"}`,
                      borderRadius: 14,
                      overflow: "hidden",
                      background: isSelected ? "#f5f3ff" : "#ffffff",
                      transition: "border-color 0.3s, background 0.3s",
                    }}>
                      {/* Card header */}
                      <div style={{
                        padding: "0.875rem 1rem",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                      }}>
                        {/* Left: checkbox + name + badges */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0 }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTestCaseSelection(id)}
                            style={{
                              width: 18,
                              height: 18,
                              cursor: "pointer",
                              flexShrink: 0,
                              accentColor: "#6366f1",
                            }}
                          />
                          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0f172a" }}>
                                🧪 {tc.name}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                fontSize: "0.68rem", fontWeight: 600,
                                background: cfg.bg, color: cfg.color,
                                border: `1px solid ${cfg.border}`,
                                borderRadius: 20, padding: "2px 7px",
                              }}>
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
                                {cfg.label}
                              </span>
                              {tc.description && (
                                <span style={{ fontSize: "0.68rem", color: "#94a3b8" }}>· {tc.description}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: run button + steps count + chevron */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                          {/* Run button */}
                          <button
                            onClick={() => runSingle(tc)}
                            disabled={isExecuting}
                            title="Run this test case"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              padding: "0.4rem 0.85rem",
                              background: isExecuting ? "#e2e8f0" : "linear-gradient(135deg, #6366f1, #818cf8)",
                              color: isExecuting ? "#94a3b8" : "white",
                              border: "none", borderRadius: 8,
                              fontSize: "0.72rem", fontWeight: 700,
                              cursor: isExecuting ? "not-allowed" : "pointer",
                              transition: "all 0.15s",
                              boxShadow: isExecuting ? "none" : "0 2px 8px #6366f130",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isExecuting
                              ? <><LuLoader size={11} style={{ animation: "spin 1s linear infinite" }} /> Running</>
                              : <><LuPlay size={11} /> Run</>
                            }
                          </button>

                          {/* Steps pill */}
                          <div style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            background: "linear-gradient(135deg, #eff6ff, #f5f3ff)",
                            border: "1px solid #ddd6fe",
                            borderRadius: 9, padding: "0.3rem 0.6rem",
                            minWidth: 44,
                          }}>
                            <span style={{ fontSize: "1rem", fontWeight: 800, color: "#4f46e5", lineHeight: 1 }}>
                              {steps.length}
                            </span>
                            <span style={{ fontSize: "0.55rem", color: "#6366f1", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              Steps
                            </span>
                          </div>

                          {/* Chevron */}
                          <button
                            onClick={() => toggleCard(id)}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              padding: 4, display: "flex", alignItems: "center",
                            }}
                          >
                            <LuChevronDown
                              size={16}
                              style={{
                                color: "#94a3b8",
                                transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
                                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Collapsible steps */}
                      <div style={{
                        overflow: "hidden",
                        maxHeight: isOpen ? "2000px" : "0px",
                        padding: isOpen ? "0.75rem 1rem" : "0 1rem",
                        borderTop: isOpen ? "1px solid #e2e8f0" : "none",
                        transition: "max-height 0.35s cubic-bezier(0.4,0,0.2,1), padding 0.35s",
                        display: "grid", gap: "0.5rem",
                      }}>
                        {steps.map((step) =>
                          step.is_physical ? (
                            <div key={step.step_number} style={{
                              padding: "0.65rem 0.875rem",
                              border: "1px solid #ede9fe",
                              borderRadius: 8,
                              background: "#faf5ff",
                              fontSize: "0.85rem",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                  <span style={{
                                    width: 20, height: 20, borderRadius: "50%",
                                    background: "#8B5CF6", color: "white",
                                    fontSize: "0.65rem", fontWeight: 700,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}>{step.step_number}</span>
                                  <strong style={{ color: "#5b21b6", fontSize: "0.8rem" }}>🦾 Physical Arm Move</strong>
                                </div>
                                <span style={{
                                  fontSize: "0.68rem", background: "#f5f3ff", color: "#7c3aed",
                                  border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 8px",
                                }}>⏱ {step.interval}ms</span>
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.3rem" }}>
                                {Object.entries(step.joint_values).map(([joint, val]) => (
                                  <div key={joint} style={{
                                    background: "white", borderRadius: 6,
                                    padding: "0.3rem 0.5rem", fontSize: "0.7rem",
                                    display: "flex", justifyContent: "space-between",
                                    border: "1px solid #ede9fe",
                                  }}>
                                    <span style={{ color: "#7c3aed" }}>{joint.replace("_", " ")}</span>
                                    <strong style={{ color: "#5b21b6" }}>{val}°</strong>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div key={step.step_number} style={{
                              padding: "0.65rem 0.875rem",
                              border: "1px solid #a5f3fc",
                              borderRadius: 8,
                              background: "#ecfeff",
                              fontSize: "0.85rem",
                            }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.4rem" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                  <span style={{
                                    width: 20, height: 20, borderRadius: "50%",
                                    background: "#06B6D4", color: "white",
                                    fontSize: "0.65rem", fontWeight: 700,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                  }}>{step.step_number}</span>
                                  <strong style={{ color: "#0e7490", fontSize: "0.8rem" }}>📡 Service Step</strong>
                                </div>
                                <span style={{
                                  fontSize: "0.68rem", background: "white", color: "#0e7490",
                                  border: "1px solid #a5f3fc", borderRadius: 20, padding: "2px 8px",
                                }}>⏱ {step.interval}ms</span>
                              </div>
                              <div style={{ display: "grid", gap: "0.2rem", marginBottom: "0.4rem" }}>
                                {[
                                  { label: "Service UUID", value: step.service_uuid },
                                  { label: "Char UUID", value: step.char_uuid },
                                ].map(row => (
                                  <div key={row.label} style={{
                                    display: "flex", justifyContent: "space-between",
                                    fontSize: "0.7rem", padding: "0.2rem 0",
                                    borderBottom: "1px solid #cffafe",
                                  }}>
                                    <span style={{ color: "#0e7490" }}>{row.label}</span>
                                    <span style={{ fontFamily: "monospace", color: "#0f172a", fontWeight: 500, fontSize: "0.68rem" }}>
                                      {row.value}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div style={{ display: "flex", gap: "0.375rem", flexWrap: "wrap" }}>
                                {[
                                  { label: "Read", value: step.can_read },
                                  { label: "Write", value: step.can_write },
                                  { label: "Notify", value: step.can_notify },
                                ].map(cap => (
                                  <span key={cap.label} style={{
                                    fontSize: "0.68rem", fontWeight: 600,
                                    padding: "2px 8px", borderRadius: 20,
                                    background: cap.value ? "#f0fdf4" : "white",
                                    color: cap.value ? "#16a34a" : "#94a3b8",
                                    border: `1px solid ${cap.value ? "#bbf7d0" : "#e2e8f0"}`,
                                  }}>
                                    {cap.value ? "✓" : "✗"} {cap.label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Results Modal ── */}
          {showResultsModal && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}>
              <div style={{
                background: "white",
                borderRadius: 16,
                padding: "2rem",
                maxWidth: "800px",
                maxHeight: "85vh",
                overflow: "auto",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              }}>
                {/* Modal Header */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1.5rem",
                }}>
                  <h2 style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "#0f172a",
                    margin: 0,
                  }}>
                    {batchResults?.success ? "✓ Tests Completed" : "✗ Tests Failed"}
                  </h2>
                  <button
                    onClick={() => setShowResultsModal(false)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "0.5rem",
                    }}
                  >
                    <LuX size={24} color="#94a3b8" />
                  </button>
                </div>

                {/* Message */}
                <p style={{
                  color: "#64748b",
                  fontSize: "0.95rem",
                  marginBottom: "1.5rem",
                }}>
                  {batchResults?.message || "Batch execution completed."}
                </p>

                {/* Results Table */}
                {batchResults?.success && batchResults?.batch_report && (
                  <div style={{
                    overflowX: "auto",
                    marginBottom: "1.5rem",
                  }}>
                    <table style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "0.9rem",
                    }}>
                      <thead>
                        <tr style={{
                          borderBottom: "2px solid #e2e8f0",
                          background: "#f8fafc",
                        }}>
                          <th style={{
                            padding: "0.75rem",
                            textAlign: "left",
                            fontWeight: 700,
                            color: "#0f172a",
                          }}>
                            Test Case ID
                          </th>
                          <th style={{
                            padding: "0.75rem",
                            textAlign: "left",
                            fontWeight: 700,
                            color: "#0f172a",
                          }}>
                            Status
                          </th>
                          <th style={{
                            padding: "0.75rem",
                            textAlign: "left",
                            fontWeight: 700,
                            color: "#0f172a",
                          }}>
                            Steps Executed
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchResults.batch_report.map((report) => (
                          <tr key={report.test_case_id} style={{
                            borderBottom: "1px solid #e2e8f0",
                          }}>
                            <td style={{
                              padding: "0.75rem",
                              color: "#475569",
                              fontWeight: 600,
                            }}>
                              #{report.test_case_id}
                            </td>
                            <td style={{
                              padding: "0.75rem",
                            }}>
                              <span style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                padding: "0.3rem 0.75rem",
                                borderRadius: 20,
                                background: report.success ? "#f0fdf4" : "#fef2f2",
                                color: report.success ? "#16a34a" : "#dc2626",
                                border: `1px solid ${report.success ? "#bbf7d0" : "#fecaca"}`,
                              }}>
                                {report.success ? "✓ Success" : "✗ Failed"}
                              </span>
                            </td>
                            <td style={{
                              padding: "0.75rem",
                              color: "#475569",
                            }}>
                              {report.results?.length || 0} / {report.results?.length || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Detailed Results */}
                {batchResults?.success && batchResults?.batch_report && (
                  <div style={{
                    marginBottom: "1.5rem",
                  }}>
                    <h3 style={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: "0.75rem",
                    }}>
                      Step Details
                    </h3>
                    {batchResults.batch_report.map((report) => (
                      <div key={report.test_case_id} style={{
                        marginBottom: "1rem",
                        padding: "1rem",
                        background: "#f8fafc",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                      }}>
                        <h4 style={{
                          fontSize: "0.95rem",
                          fontWeight: 700,
                          color: "#0f172a",
                          marginBottom: "0.5rem",
                        }}>
                          Test Case #{report.test_case_id}
                        </h4>
                        {report.results?.map((result, idx) => (
                          <div key={idx} style={{
                            padding: "0.5rem",
                            background: result.status === "success" ? "#f0fdf4" : "#fef2f2",
                            border: `1px solid ${result.status === "success" ? "#bbf7d0" : "#fecaca"}`,
                            borderRadius: 6,
                            marginBottom: "0.5rem",
                            fontSize: "0.85rem",
                            color: result.status === "success" ? "#15803d" : "#991b1b",
                          }}>
                            <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                              Step {result.step} - {result.type}
                            </div>
                            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                              {result.status === "success" ? "✓ Success" : "✗ Failed"}
                            </div>
                            {result.data && (
                              <div style={{
                                fontSize: "0.75rem",
                                marginTop: "0.25rem",
                                fontFamily: "monospace",
                                opacity: 0.7,
                              }}>
                                {JSON.stringify(result.data, null, 2)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Error Message */}
                {!batchResults?.success && batchResults?.error && (
                  <div style={{
                    padding: "1rem",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    color: "#991b1b",
                    marginBottom: "1.5rem",
                    fontSize: "0.9rem",
                  }}>
                    <strong>Error:</strong> {batchResults.error}
                  </div>
                )}

                {/* Close Button */}
                <div style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.75rem",
                }}>
                  <button
                    onClick={() => setShowResultsModal(false)}
                    style={{
                      padding: "0.625rem 1.25rem",
                      background: "linear-gradient(135deg, #6366f1, #818cf8)",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      fontSize: "0.9rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          <WizardActions
            showBack={true}
            onBack={handleBack}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue →"
          />
        </WizardCard>
      </WizardContainer>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </PrivateWizardLayout>
  );
};

export default Step12IotRun;